import * as React from "react";
import CheckboxTree from "react-checkbox-tree";
import { Form } from "react-bootstrap";
import { ExpandopenIcon } from "./ui/icons";
import { ITreeNode } from "../common/types/commonTypes";
import { IGroupAlerts, ISelectGroupState } from "../common/types/alertsTypes";

interface ICascadeGroupTreeViewProps {
  nodes: ITreeNode[];
  expandedValues: string[];
  setExpandedValues: React.Dispatch<React.SetStateAction<string[]>>;
  selectGroupState: ISelectGroupState;
  setSelectGroupState: (state: ISelectGroupState) => void;
  onDataFromTreeView: (selectedGroups: IGroupAlerts[]) => void;
  isSelectAllEnabled?: boolean;
}

type TreeNodeWithTitle = ITreeNode & {
  title?: string;
  children?: TreeNodeWithTitle[];
};

const addTitleToNodes = (nodes: ITreeNode[]): TreeNodeWithTitle[] => {
  return nodes.map((node) => {
    const nodeWithTitle: TreeNodeWithTitle = {
      ...node,
      title: String(node.label),
    };

    if (Array.isArray(node.children)) {
      nodeWithTitle.children = addTitleToNodes(node.children);
    }

    return nodeWithTitle;
  });
};

const getNodeValue = (node: ITreeNode): string => {
  return node.value;
};

const getNodeLabel = (node: ITreeNode): string => {
  return String(node.label);
};

const getAllNodeValues = (nodes: ITreeNode[]) => {
  const allNodeValues: string[] = [];

  const traverseNode = (node: ITreeNode) => {
    allNodeValues.push(getNodeValue(node));

    if (node.children?.length) {
      for (const childNode of node.children) {
        traverseNode(childNode);
      }
    }
  };

  for (const node of nodes) {
    traverseNode(node);
  }

  return allNodeValues;
};

const getSelectedGroupsFromCheckedValues = (
  nodes: ITreeNode[],
  checkedValues: string[],
) => {
  const checkedValueSet = new Set(checkedValues);
  const selectedGroups: IGroupAlerts[] = [];

  const traverseNode = (node: ITreeNode) => {
    const nodeValue = getNodeValue(node);

    if (checkedValueSet.has(nodeValue)) {
      selectedGroups.push({
        id: nodeValue,
        displayName: getNodeLabel(node),
      });
    }

    if (node.children?.length) {
      for (const childNode of node.children) {
        traverseNode(childNode);
      }
    }
  };

  for (const node of nodes) {
    traverseNode(node);
  }

  return selectedGroups;
};

const areStringArraysEqual = (firstArray: string[], secondArray: string[]) => {
  if (firstArray.length !== secondArray.length) {
    return false;
  }

  const secondValueSet = new Set(secondArray);
  return firstArray.every((value) => secondValueSet.has(value));
};

const collectNodeAndChildrenValues = (node: ITreeNode): string[] => {
  const values: string[] = [String(node.value)];

  if (node.children?.length) {
    for (const childNode of node.children) {
      values.push(...collectNodeAndChildrenValues(childNode));
    }
  }

  return values;
};

const getCascadeCheckedValues = (
  nodes: ITreeNode[],
  checkedValues: string[],
): string[] => {
  const checkedValueSet = new Set(checkedValues.map(String));
  const cascadeCheckedValues = new Set<string>();

  const traverseNode = (node: ITreeNode) => {
    const nodeValue = String(node.value);

    if (checkedValueSet.has(nodeValue)) {
      const nodeAndChildrenValues = collectNodeAndChildrenValues(node);

      for (const checkedValue of nodeAndChildrenValues) {
        cascadeCheckedValues.add(checkedValue);
      }
    }

    if (node.children?.length) {
      for (const childNode of node.children) {
        traverseNode(childNode);
      }
    }
  };

  for (const node of nodes) {
    traverseNode(node);
  }

  return Array.from(cascadeCheckedValues);
};

const CascadeGroupTreeView = ({
  nodes,
  expandedValues,
  setExpandedValues,
  selectGroupState,
  setSelectGroupState,
  onDataFromTreeView,
  isSelectAllEnabled = false,
}: ICascadeGroupTreeViewProps) => {
  const [checkedValues, setCheckedValues] = React.useState<string[]>(
    selectGroupState?.checked || [],
  );


  const allNodeValues = React.useMemo(() => {
    return getAllNodeValues(nodes);
  }, [nodes]);

  const nodesWithTitle = React.useMemo(() => {
    return addTitleToNodes(nodes);
  }, [nodes]);

  const isSelectAllChecked =
    allNodeValues.length > 0 && checkedValues.length === allNodeValues.length;

  const updateCheckedGroups = React.useCallback(
    (nextCheckedValues: string[]) => {
      const allCurrentNodeValues = new Set(getAllNodeValues(nodes));
      const filteredOutCheckedValues = (selectGroupState?.checked || []).filter(
        (val) => !allCurrentNodeValues.has(val)
      );

      const finalCheckedValues = Array.from(
        new Set([...nextCheckedValues, ...filteredOutCheckedValues])
      );

      const selectedGroups = getSelectedGroupsFromCheckedValues(
        nodes,
        nextCheckedValues,
      );

      const filteredOutGroups = (selectGroupState?.groups || []).filter(
        (g) => !allCurrentNodeValues.has(g.id)
      );

      const finalSelectedGroups = [
        ...selectedGroups,
        ...filteredOutGroups,
      ];

      setCheckedValues(finalCheckedValues);

      setSelectGroupState({
        ...selectGroupState,
        checked: finalCheckedValues,
        groups: finalSelectedGroups,
        selectAllChecked:
          finalCheckedValues.length > 0 &&
          finalCheckedValues.length === allNodeValues.length,
      });

      onDataFromTreeView(finalSelectedGroups);
    },
    [
      nodes,
      selectGroupState,
      setSelectGroupState,
      onDataFromTreeView,
      allNodeValues.length,
    ],
  );

  const handleCheck = (nextCheckedValues: string[]) => {
    updateCheckedGroups(nextCheckedValues);
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const shouldSelectAll = event.target.checked;

    if (shouldSelectAll) {
      updateCheckedGroups(allNodeValues);
    } else {
      updateCheckedGroups([]);
    }
  };

  React.useEffect(() => {
  const incomingCheckedValues = selectGroupState?.checked || [];

  const cascadeCheckedValues = getCascadeCheckedValues(
    nodes,
    incomingCheckedValues
  );

  const allCurrentNodeValues = new Set(getAllNodeValues(nodes));
  const filteredOutCheckedValues = incomingCheckedValues.filter(
    (val) => !allCurrentNodeValues.has(val)
  );

  const finalCheckedValues = Array.from(
    new Set([...cascadeCheckedValues, ...filteredOutCheckedValues])
  );

  const selectedGroups = getSelectedGroupsFromCheckedValues(
    nodes,
    cascadeCheckedValues
  );

  const incomingGroups = selectGroupState?.groups || [];
  const filteredOutGroups = incomingGroups.filter(
    (g) => !allCurrentNodeValues.has(g.id)
  );

  const finalSelectedGroups = [
    ...selectedGroups,
    ...filteredOutGroups,
  ];

  setCheckedValues((previousCheckedValues) => {
    if (areStringArraysEqual(previousCheckedValues, finalCheckedValues)) {
      return previousCheckedValues;
    }

    return finalCheckedValues;
  });

  if (!areStringArraysEqual(incomingCheckedValues, finalCheckedValues)) {
    setSelectGroupState({
      ...selectGroupState,
      checked: finalCheckedValues,
      groups: finalSelectedGroups,
      selectAllChecked:
        finalCheckedValues.length > 0 &&
        finalCheckedValues.length === allNodeValues.length,
    });

    onDataFromTreeView(finalSelectedGroups);
  }
}, [nodes, selectGroupState?.checked, allNodeValues.length]);

  return (
    <>
      {isSelectAllEnabled && (
        <Form>
          <div>
            <Form.Check
              type="checkbox"
              id="selectall"
              label="Select All"
              checked={isSelectAllChecked}
              onChange={handleSelectAllChange}
            />
          </div>
        </Form>
      )}

      <CheckboxTree
        nodes={nodesWithTitle}
        checked={checkedValues}
        expanded={expandedValues}
        onCheck={handleCheck}
        onExpand={setExpandedValues}
        showNodeTitles={true}
        noCascade={false}
        checkModel="all"
        icons={{
          expandClose: <ExpandopenIcon />,
          expandOpen: <ExpandopenIcon />,
        }}
      />
    </>
  );
};

export default CascadeGroupTreeView;
